import React from 'react';
import { 
  Box,
  Typography, 
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Checkbox
} from '@mui/material';
import { 
  Description as DisclosuresIcon,
  HealthAndSafety as EnvironmentalIcon,
  Home as PropertyIcon,
  Gavel as LegalIcon,
  BugReport as PestIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface DisclosuresStepProps {
  stepProgress: number;
}

const DisclosuresStep: React.FC<DisclosuresStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  const yearBuilt = watch('property.yearBuilt');
  const requiresLeadDisclosure = yearBuilt && yearBuilt < 1978;

  const disclosures = [
    {
      name: 'asbestos',
      label: 'Asbestos',
      options: [
        { value: 'yes', label: 'Yes - Known asbestos present' },
        { value: 'no', label: 'No - No known asbestos' },
        { value: 'unknown', label: 'Unknown' },
      ],
    },
    {
      name: 'mold',
      label: 'Mold',
      options: [
        { value: 'none_visible', label: 'No visible mold' },
        { value: 'treated', label: 'Previously treated' },
        { value: 'present', label: 'Currently present' },
      ],
    },
    {
      name: 'methContamination',
      label: 'Methamphetamine Contamination',
      options: [
        { value: 'none', label: 'No known contamination' },
        { value: 'cleaned', label: 'Previously contaminated but cleaned' },
        { value: 'present', label: 'Known contamination present' },
      ],
    },
  ];

  const booleanDisclosures = [
    { name: 'bedBugs', label: 'Bed bugs in the last year' },
    { name: 'deathOnProperty', label: 'Death on property (last 3 years)' },
    { name: 'militarySite', label: 'Within 1 mile of former military training site' },
    { name: 'floodZone', label: 'Property in flood hazard zone' },
    { name: 'earthquakeFault', label: 'Property in earthquake fault zone' },
    { name: 'noiseIssues', label: 'Known noise issues in area' },
  ];

  return (
    <StepFormLayout
      title="California Required Disclosures"
      description="Complete required California rental property disclosures"
      icon={<DisclosuresIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={stepProgress}
      showOptionalBadge={true}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>⚖️ Tip:</strong> California law requires specific disclosures for rental properties. Complete accuracy is essential.
        </Typography>
      </Alert>
      
      {requiresLeadDisclosure && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            <strong>⚠️ Required:</strong> This property was built before 1978 and requires lead-based paint disclosure.
          </Typography>
        </Alert>
      )}
      
      {/* Lead Paint Disclosure */}
      {requiresLeadDisclosure && (
        <FormSection
          title="Lead-Based Paint Disclosure"
          icon={<WarningIcon color="primary" />}
          tooltip="Required for properties built before 1978"
        >
          <Stack spacing={3}>
            <Controller
              name="disclosures.leadPaint.knownLeadPaint"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Known lead-based paint present"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No known lead-based paint"
                  />
                </RadioGroup>
              )}
            />
            <FormTextField
              name="disclosures.leadPaint.reports"
              control={control}
              label="Lead Paint Reports/Records (Optional)"
              multiline
              rows={2}
            />
          </Stack>
        </FormSection>
      )}

        {/* Environmental Disclosures */}
        <FormSection
          title="Environmental Disclosures"
          icon={<EnvironmentalIcon color="primary" />}
          tooltip="Required environmental hazard disclosures"
        >
          <Stack spacing={3}>
            {disclosures.map((disclosure) => (
              <Box key={disclosure.name}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  {disclosure.label}
                </Typography>
                <Controller
                  name={`disclosures.${disclosure.name}.status`}
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      {disclosure.options.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormTextField
                  name={`disclosures.${disclosure.name}.details`}
                  control={control}
                  label="Additional Details (Optional)"
                  multiline
                  rows={2}
                />
              </Box>
            ))}
          </Stack>
        </FormSection>
        
        {/* Additional Property Disclosures */}
        <FormSection
          title="Additional Property Disclosures"
          icon={<PropertyIcon color="primary" />}
          tooltip="Other property-specific disclosures required by law"
        >
          <Stack spacing={3}>
            {booleanDisclosures.map((disclosure) => (
              <Box key={disclosure.name}>
                <Controller
                  name={`disclosures.${disclosure.name}.${disclosure.name === 'bedBugs' ? 'history' : 
                        disclosure.name === 'deathOnProperty' ? 'occurred' : 
                        disclosure.name === 'militarySite' ? 'within1Mile' :
                        disclosure.name === 'floodZone' ? 'inFloodZone' :
                        disclosure.name === 'earthquakeFault' ? 'inZone' :
                        'hasIssues'}`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={disclosure.label}
                    />
                  )}
                />
                <FormTextField
                  name={`disclosures.${disclosure.name}.details`}
                  control={control}
                  label="Details (if applicable)"
                  multiline
                  rows={2}
                />
              </Box>
            ))}
          </Stack>
        </FormSection>

        {/* Legal Disclosures */}
        <FormSection
          title="Legal Protections & Requirements"
          icon={<LegalIcon color="primary" />}
          tooltip="Tenant Protection Act and legal requirements"
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Tenant Protection Act of 2019
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The TPA limits most annual rent increases to CPI + 5% and requires just cause to terminate once a tenant has occupied the unit for 12 months. Properties can be exempt when they meet criteria such as:
              </Typography>
              <Box component="ul" sx={{ mt: 0, pl: 3, mb: 2 }}>
                <Typography component="li" variant="body2">New construction (within 15 years of its certificate of occupancy)</Typography>
                <Typography component="li" variant="body2">Owner-occupied duplexes or single-family homes not owned by a corporation, REIT, or corporate LLC</Typography>
                <Typography component="li" variant="body2">Deed-restricted affordable housing, dormitories, or certain specialized housing (e.g., licensed care)</Typography>
              </Box>
              <Controller
                name="disclosures.tpa.overallStatus"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="applies" control={<Radio />} label="Applies" />
                    <FormControlLabel value="exempt" control={<Radio />} label="Exempt" />
                    <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
                  </RadioGroup>
                )}
              />
              {watch('disclosures.tpa.overallStatus') === 'exempt' && (
                <FormTextField
                  name="disclosures.tpa.exemptionReason"
                  control={control}
                  label="Exemption Reason"
                  required
                  helperText="Briefly explain which statutory exemption applies"
                />
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Use the options below to document whether rent caps and just-cause protections apply to this tenancy.
              </Typography>

              <Typography variant="body2" sx={{ mt: 3, fontWeight: 600 }}>
                Rent Cap Applicability
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Indicates whether the statewide rent cap (generally 5% + CPI) applies. You may be exempt if:
              </Typography>
              <Box component="ul" sx={{ mt: 0, pl: 3, mb: 1 }}>
                <Typography component="li" variant="body2">Certificate of occupancy issued within the last 15 years</Typography>
                <Typography component="li" variant="body2">Single-family home or condo not owned by a corporation, REIT, or corporate LLC</Typography>
                <Typography component="li" variant="body2">Deed-restricted affordable housing or dormitory accommodations</Typography>
              </Box>
              <Controller
                name="disclosures.tpa.rentCapStatus"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="applies" control={<Radio />} label="Applies" />
                    <FormControlLabel value="exempt" control={<Radio />} label="Exempt" />
                    <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
                  </RadioGroup>
                )}
              />

              <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
                Just Cause Requirement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose whether Civil Code §1946.2 just-cause eviction protections apply. Exemptions often mirror the rent-cap rules and may also include:
              </Typography>
              <Box component="ul" sx={{ mt: 0, pl: 3, mb: 1 }}>
                <Typography component="li" variant="body2">Duplexes where the owner occupies another unit</Typography>
                <Typography component="li" variant="body2">Transient lodging such as hotels or licensed care facilities</Typography>
                <Typography component="li" variant="body2">Student housing or other specialized programs</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If protections apply, terminating the tenancy demands a qualifying reason.
              </Typography>
              <Controller
                name="disclosures.tpa.justCauseStatus"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="applies" control={<Radio />} label="Applies" />
                    <FormControlLabel value="exempt" control={<Radio />} label="Exempt" />
                    <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
                  </RadioGroup>
                )}
              />
            </Box>
          </Stack>
        </FormSection>

        {/* Pest Control */}
        <FormSection
          title="Pest Control Services"
          icon={<PestIcon color="primary" />}
          tooltip="Pest control treatment and service information"
        >
          <Stack spacing={3}>
            <Controller
              name="disclosures.pestControl.regularTreatment"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="Regular pest control treatment provided"
                />
              )}
            />
            {watch('disclosures.pestControl.regularTreatment') && (
              <FormTextField
                name="disclosures.pestControl.frequency"
                control={control}
                label="Treatment Frequency"
              />
            )}
          </Stack>
        </FormSection>
        

      </Box>
    </StepFormLayout>
  );
};

export default DisclosuresStep; 
